package com.xtream.data_kmp.remote.repo

import com.xtream.data_kmp.AllConst
import com.xtream.data_kmp.AppConfig
import com.xtream.data_kmp.PreferencesRepo
import com.xtream.data_kmp.db.repo.LiveStreamsRepository
import com.xtream.data_kmp.db.repo.SeriesStreamsRepository
import com.xtream.data_kmp.db.repo.UserRepo
import com.xtream.data_kmp.db.repo.VodStreamsRepository
import com.xtream.data_kmp.dto.SeriesCategory
import com.xtream.data_kmp.dto.ServerConfig
import com.xtream.data_kmp.dto.TrendingMovie
import com.xtream.data_kmp.dto.TrendingTvShow
import com.xtream.data_kmp.dto.VodCategory
import com.xtream.data_kmp.dto.XtreamActivationBaseUserInfo
import com.xtream.data_kmp.remote.ApiService
import com.xtream.data_kmp.remote.createApiService
import com.xtream.data_kmp.util.exception.UserNotFoundException
import io.ktor.client.call.body
import io.ktor.client.statement.HttpResponse
import io.ktor.http.isSuccess
import kotlinx.coroutines.CoroutineExceptionHandler
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.NonCancellable
import kotlinx.coroutines.joinAll
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import kotlinx.serialization.builtins.ListSerializer
import kotlinx.serialization.json.JsonObject

class XtreamRemoteRepo(
    private val tvStreamsRepo: LiveStreamsRepository,
    private val vodStreamsRepo: VodStreamsRepository,
    private val seriesStreamsRepo: SeriesStreamsRepository,
    private val userRepo: UserRepo,
    private val appSettingsRepo: PreferencesRepo
) {
    val coroutineExceptionHandler = CoroutineExceptionHandler { _, throwable ->
        throwable.printStackTrace()
    }

    // ✅ Context removed — caller provides deviceInfo
    fun loadXtreamAccount(
        scope: CoroutineScope,
        config: ServerConfig?,
        deviceInfo: DeviceInfo,
        onSuccess: (suspend () -> Unit)? = null,
        onFail: ((Throwable) -> Unit)? = null
    ) {
        scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            val account = userRepo.getLastLoggedInUser()
            if (account?.userInfo?.activationCode != null) {
                checkByActivationCode(
                    scope,
                    account.userInfo.activationCode,
                    deviceInfo,
                    onSuccess,
                    onFail
                )
            } else {
                loadUser(
                    scope,
                    account?.getUserServerUrl() ?: config?.accountServerUrl,
                    account?.userInfo?.username ?: config?.trialUsername,
                    account?.userInfo?.password ?: config?.trialPassword,
                    onSuccess,
                    onFail
                )
            }
        }
    }

    fun checkByActivationCode(
        scope: CoroutineScope,
        activationCodeVal: String?,
        deviceInfo: DeviceInfo,  // ✅ replaces Context
        onSuccess: (suspend () -> Unit)? = null,
        onFail: ((Throwable) -> Unit)? = null
    ) {
        scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            try {
                if (activationCodeVal.isNullOrEmpty())
                    throw Exception("Error: code is empty")

                val data = """{"mode":"active","code":"$activationCodeVal","mac":"${deviceInfo.macAddress}","sn":"${deviceInfo.deviceId}","chipid":"","model":"${deviceInfo.model}","firmware_ver":"${deviceInfo.firmwareVersion}"}"""
                val key = appSettingsRepo.getPref(PreferencesRepo.ACTIVATION_XOR_KEY)
                val encryptedData = com.xtream.data_kmp.db.utils.XorEncDec.encDec(msg = data, key = key ?: "")
                val baseUrl = appSettingsRepo.getPref(PreferencesRepo.ACTIVATION_SERVER_URL_KEY)
                    ?: throw Exception("Error: No Activation Server Available!")

                val client = createApiService(baseUrl)
                val response: HttpResponse = client.activate(encryptedData)

                if (response.status.isSuccess()) {
                    val decryptedResponse = com.xtream.data_kmp.db.utils.XorEncDec.encDec(response.body(), key = key ?: "")
                    val body = ApiService.jsonObj.decodeFromString<XtreamActivationBaseUserInfo>(decryptedResponse)
                    body.userInfo?.activationCode = activationCodeVal

                    if (body.userInfo?.status in listOf("100", "101")) {
                        body.toXtreamUser()?.also { userRepo.setAsLoggedIn(it) }
                        onSuccess?.invoke()
                    } else if (body.userInfo?.status.equals("4")) {
                        throw Exception(AllConst.ACCOUNT_EXPIRED_EXCEPTION)
                    } else if (listOf("7").contains(body.userInfo?.status)) {
                        throw UserNotFoundException(
                            buildString {
                                append(AllConst.ACCOUNT_UNAVAILABLE_EXCEPTION)
                                append(body.userInfo?.let { "\nCode: $activationCodeVal\n\nStatus: ${it.message} (Error Code: ${it.status})\n" })
                            }
                        )
                    } else {
                        throw UserNotFoundException("Code Unavailable, Error code: ${body.userInfo?.status}")
                    }
                } else {
                    throw Exception("Error code ${response.status}")
                }
            } catch (e: Exception) {
                onFail?.invoke(e)
            }
        }
    }

    fun loadUser(
        scope: CoroutineScope,
        serverUrl: String?,
        username: String?,
        password: String?,
        onSuccess: (suspend () -> Unit?)? = null,
        onFail: (suspend (Throwable) -> Unit?)? = null,
        needInsertUser: Boolean = true
    ) {
        scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            try {
                if (username.isNullOrEmpty() || password.isNullOrEmpty())
                    throw Exception("Error: username or password is empty")
                if (serverUrl.isNullOrEmpty())
                    throw Exception("Error: No Server")

                val client = createApiService("$serverUrl${AppConfig.SERVER_API}", username, password)
                val response = client.login()

                if (response.status.isSuccess()) {
                    val body = response.body<com.xtream.data_kmp.dto.Account>()
                    if (body.userInfo.auth != 1) {
                        if (body.userInfo.auth == 0) throw UserNotFoundException()
                        else throw UserNotFoundException("Error code ${response.status}")
                    } else {
                        when (body.userInfo.status?.lowercase()) {
                            "expired" -> throw UserNotFoundException(AllConst.ACCOUNT_EXPIRED_EXCEPTION)
                            "active" -> {
                                if (needInsertUser) userRepo.setAsLoggedIn(body)
                                onSuccess?.invoke()
                            }
                            else -> throw UserNotFoundException(AllConst.ACCOUNT_UNAVAILABLE_EXCEPTION)
                        }
                    }
                } else {
                    if (response.status.value == 404) {
                        val loggedInUser = userRepo.getLastLoggedInUser()
                        throw UserNotFoundException(
                            buildString {
                                append(AllConst.ACCOUNT_UNAVAILABLE_EXCEPTION)
                                append("\n")
                                append((loggedInUser?.userInfo?.activationCode ?: loggedInUser?.userInfo?.username) ?: "")
                            }
                        )
                    } else {
                        throw UserNotFoundException("Error code: ${response.status}")
                    }
                }
            } catch (e: Exception) {
                onFail?.invoke(e)
            }
        }
    }

    fun loadStreams(
        scope: CoroutineScope,
        isDateTimeValid: Boolean = true,
        onComplete: (Throwable?) -> Unit,
    ) {
        scope.launch(NonCancellable) {
            launch(coroutineExceptionHandler + NonCancellable) {
                withContext(NonCancellable) {
                    val account = userRepo.getLastLoggedInUser()
                    val serverUrl = account?.getUserServerUrl() ?: ""
                    val username = account?.userInfo?.username ?: ""
                    val password = account?.userInfo?.password ?: ""
                    val apiService = createApiService("$serverUrl${AppConfig.SERVER_API}", username, password)

                    val listOfJobs = listOf(
                        loadLiveStreams(apiService, scope),
                        loadVodStreams(apiService, scope),
                        loadSeriesStreams(apiService, scope)
                    )
                    listOfJobs.forEach { streamsJob ->
                        streamsJob.joinAll()
                        streamsJob.forEach { itemsJob ->
                            itemsJob.invokeOnCompletion {
                                when (it) {
                                    null -> {}
                                    else -> throw it
                                }
                            }
                        }
                    }
                }
            }.invokeOnCompletion {
                onComplete(it)
                println("outer invokeOnCompletion$it")
            }
        }
    }

    fun loadLiveStreams(apiService: ApiService, scope: CoroutineScope): List<Job> {
        return try {
            val streamsJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
                val streams = apiService.getLiveStreams()
                streams?.also { tvStreamsRepo.refreshData(it) }
            }
            val categoriesJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
                val categories = apiService.getLiveCategories()
                categories?.also { tvStreamsRepo.refreshCategories(it) }
            }
            listOf(categoriesJob, streamsJob)
        } catch (e: Exception) {
            throw e
        }
    }

    fun loadVodStreams(apiService: ApiService, scope: CoroutineScope): List<Job> {
        val streamsJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            val vodStreams = apiService.getVodStreams()
            vodStreams?.also { vodStreamsRepo.refreshData(it) }
        }
        val categoriesJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            val watched = VodCategory(AllConst.WATCHED_CATEGORIES_ID.toInt(), "Continue Watching")
            val favCategory = VodCategory(AllConst.FAV_CATEGORIES_ID.toInt(), "Favorites")
            val vodCategories = apiService.getVodCategories()
            vodCategories?.also { vodStreamsRepo.refreshCategories(listOf(favCategory, watched) + it) }
        }
        scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            try {
                val tmdbKey = appSettingsRepo.getPref(PreferencesRepo.SERVER_TMDB_KEY) ?: ""
                val trending = apiService.tmdbTrendingMovies(tmdbKey)
                if (trending.status.isSuccess()) {
                    val json = trending.body<JsonObject>()
                    val results = json["results"]
                    val vodList: List<TrendingMovie>? = results?.let {
                        ApiService.jsonObj.decodeFromJsonElement(ListSerializer(TrendingMovie.serializer()), it)
                    }
                    vodStreamsRepo.refreshTrending(vodList)
                }
            } catch (e: Exception) {}
        }
        return listOf(categoriesJob, streamsJob)
    }

    fun loadSeriesStreams(apiService: ApiService, scope: CoroutineScope): List<Job> {
        val streamsJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            val streams = apiService.getSeriesStreams()
            streams?.also { seriesStreamsRepo.refreshData(it) }
        }
        val categoriesJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            val watched = SeriesCategory(AllConst.WATCHED_CATEGORIES_ID.toInt(), "Continue Watching")
            val favCategory = SeriesCategory(AllConst.FAV_CATEGORIES_ID.toInt(), "Favorites")
            val categories = apiService.getSeriesCategories()
            categories?.also { seriesStreamsRepo.refreshCategories(listOf(favCategory, watched) + it) }
        }
        val trendingJob = scope.launch(Dispatchers.Default + coroutineExceptionHandler) {
            try {
                val tmdbKey = appSettingsRepo.getPref(PreferencesRepo.SERVER_TMDB_KEY) ?: ""
                val trending = apiService.tmdbTrendingTvShows(tmdbKey)
                if (trending.status.isSuccess()) {
                    val json = trending.body<JsonObject>()
                    val results = json["results"]
                    val tvShows: List<TrendingTvShow>? = results?.let {
                        ApiService.jsonObj.decodeFromJsonElement(ListSerializer(TrendingTvShow.serializer()), it)
                    }
                    seriesStreamsRepo.refreshTrending(tvShows)
                }
            } catch (e: Exception) {}
        }
        return listOf(categoriesJob, streamsJob, trendingJob)
    }
}